/**
 * ConfirmationReviewModal - Review and confirm/decline structural changes
 * 
 * ARC Doctrine: Calm language, no urgency, no blame
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";
import { 
  CheckCircle2, 
  X, 
  Loader2,
  Clock,
  Shield,
  Settings,
  Trash2,
  Archive,
  AlertTriangle
} from "lucide-react";

// Action type display info
const ACTION_TYPE_INFO = {
  change_privacy_style: {
    icon: Shield,
    label: "Privacy settings",
    impactLevel: "medium",
    impactText: "This will change how your data is shared between partners."
  },
  delete_account: {
    icon: Trash2,
    label: "Delete account",
    impactLevel: "high",
    impactText: "This will permanently remove the account and its transaction history."
  },
  archive_account: {
    icon: Archive,
    label: "Archive account",
    impactLevel: "low",
    impactText: "The account will be hidden from active views but preserved."
  },
  change_cadence: {
    icon: Clock,
    label: "Review schedule",
    impactLevel: "low",
    impactText: "This will change how often you do your review & reset."
  },
  change_budget_structure: {
    icon: Settings,
    label: "Budget structure",
    impactLevel: "medium",
    impactText: "This will change how your budget categories are organized."
  }
};

const IMPACT_STYLES = {
  low: "bg-blue-100 text-blue-700 border-blue-300",
  medium: "bg-amber-100 text-amber-700 border-amber-300",
  high: "bg-red-100 text-red-700 border-red-300"
};

export function ConfirmationReviewModal({ 
  open, 
  onOpenChange, 
  action,
  onResolved
}) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null); // 'approve' or 'decline'

  if (!action) return null;

  const typeInfo = ACTION_TYPE_INFO[action.action_type] || {
    icon: AlertTriangle,
    label: action.action_type,
    impactLevel: "medium",
    impactText: "This change requires your confirmation."
  };
  const Icon = typeInfo.icon;

  const handleDecision = async (approve) => {
    setDecision(approve ? "approve" : "decline");
    setLoading(true);
    
    try {
      const endpoint = approve 
        ? `/api/household/action/${action.action_id}/approve`
        : `/api/household/action/${action.action_id}/decline`;
      
      const res = await api.post(endpoint, { reason: reason.trim() || null });
      
      if (res.ok) {
        toast.success(approve ? "Change approved" : "Change declined");
        onResolved?.(approve ? "approved" : "declined", action.action_id);
        handleClose();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Could not process decision");
      }
    } catch (err) {
      console.error("Decision error:", err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
      setDecision(null);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason("");
      setDecision(null);
      onOpenChange(false);
    }
  };

  const formatPayload = (payload) => {
    return Object.entries(payload)
      .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${value}`)
      .join(", ");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="confirmation-review-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-amber-600" />
            Review requested change
          </DialogTitle>
          <DialogDescription>
            Your partner has requested a change that needs your confirmation.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Change details */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">{typeInfo.label}</span>
              <Badge className={IMPACT_STYLES[typeInfo.impactLevel]}>
                {typeInfo.impactLevel} impact
              </Badge>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {typeInfo.impactText}
            </p>
            
            <div className="pt-2 border-t">
              <p className="text-sm">
                <span className="text-muted-foreground">Proposed change: </span>
                <span className="font-medium">{formatPayload(action.payload)}</span>
              </p>
              
              {action.reason && (
                <p className="text-sm mt-1">
                  <span className="text-muted-foreground">Reason given: </span>
                  <span>{action.reason}</span>
                </p>
              )}
              
              <p className="text-sm mt-1 text-muted-foreground">
                Requested by {action.initiated_by_name || "Partner"}
              </p>
            </div>
          </div>
          
          {/* Optional reason */}
          <div className="space-y-2">
            <Label htmlFor="decision-reason">
              Add a note <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="decision-reason"
              placeholder="Any thoughts on this change..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="resize-none"
              rows={2}
              maxLength={200}
              disabled={loading}
              data-testid="decision-reason-input"
            />
          </div>
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => handleDecision(false)}
            disabled={loading}
            className="border-slate-300"
            data-testid="decline-btn"
          >
            {loading && decision === "decline" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <X className="w-4 h-4 mr-2" />
            )}
            Decline
          </Button>
          <Button
            onClick={() => handleDecision(true)}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="approve-btn"
          >
            {loading && decision === "approve" ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Approve change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmationReviewModal;
